import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BottomSection,
  Buttons,
  DescriptionSection,
  Detail,
  Group,
  HeaderText,
  HeaderTitle,
  Inputs,
  Leftbuttons,
  OverLayDiv,
  TaskTemplateHeader,
  Textarea,
  TextField,
  UploadDiv
} from './styles';
import AppLayout from '../appLayout';
import Button from '../button';
import isEmpty from 'lodash/isEmpty';
import { handleFileUpload, removeFileFromFirebase } from '../../helpers/common';
import {
  createNewTaskTemplate,
  dispatchSettingError,
  dispatchSettingSuccess,
  getTaskTemplateList,
  updateTaskTemplateDetails
} from '../../services/settingServices';
import { useHistory, useParams } from 'react-router-dom';
import { ROUTES } from '../../routes/urls';
import { BUTTON_TYPE, FILE_TYPES, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE } from '../../global/constants';
import { captureException } from '../../services/logService';
import { rootStore } from '../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import PriorityDropdownV2 from '../dropdowns/priorityDropdown';
import AssignBoxV2 from '../dropdowns/assignPopup';
import { useTaskTemplateAssignActions } from '../dropdowns/assignPopup/assignActions';
import { TaskTemplateModel } from '../../mobx/stores/projectTemplateStore';
import { RawFile } from '../../mobx/interfaces/task';
import { useMobileDevice } from '../../global/useMobile';
import ResponsiveHeader from '../responsiveHeader';
import BottomSubNavigation from '../bottomNavigation/subNavigation';
import BottomNavigationItem from '../bottomNavigation/subNavigation/bottomNavigationItem';
import { Container } from '../../pages/settings/styles';
import IconButton from '../button/iconButton';
import TiptapEditor from '../tiptapEditor';
import { RenderFilePreview } from '../renderFilePreview';
import Input from '../input';
import { TiptapEditorRef } from '../../interfaces/tiptapInterface';

const TaskTemplateDetails: React.FC = () => {
  //Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inlineRef = useRef<TiptapEditorRef>(null);
  //States
  const [fileUploading, setFileUploading] = useState(false);
  const [isAssignDropdownOpen, setIsAssignDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updatedFile, setUpdatedFile] = useState<RawFile[]>([]);
  const [priorityVersion, setPriorityVersion] = useState(0);

  //Mobx store variables
  const { projectTemplateStore, workspaceStore, projectStore } = rootStore;
  const { currentTaskTemplate } = projectTemplateStore;
  const { currentWorkspace } = workspaceStore;
  const { uploadFilePercent } = projectStore;
  const assignActions = useTaskTemplateAssignActions();

  //Other variable
  const history = useHistory();
  const params: { id: string } = useParams();
  const isMobile = useMobileDevice();

  const loadData = useCallback(async () => {
    if (isEmpty(currentTaskTemplate?.id) && currentWorkspace?.id) {
      try {
        await getTaskTemplateList();
        rootStore.projectTemplateStore.setCurrentTaskTemplate(params?.id);
      } catch (error) {
        captureException(error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.id, currentWorkspace?.id]);

  useEffect(() => {
    if (updatedFile && updatedFile.length > 0) {
      currentTaskTemplate?.setFiles(updatedFile);
    }
  }, [currentTaskTemplate, updatedFile]);

  useEffect(() => {
    loadData();
    return () => {
      projectTemplateStore.setCurrentTaskTemplate('');
    };
  }, [loadData, projectTemplateStore]);

  useEffect(() => {
    setPriorityVersion((prev) => prev + 1);
  }, [currentTaskTemplate?.priority]);

  const handleFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files || files.length === 0) {
        dispatchSettingError('No files selected.');
        return;
      }
      try {
        setFileUploading(true);
        const fileArray = Array.from(files); // Convert FileList to Array
        const updatedFiles = await handleFileUpload(currentTaskTemplate?.files || [], fileArray, inlineRef, undefined, 'templates/tasks/'); //call common function to upload file in firebase under templates/tasks/
        const value = [...(currentTaskTemplate?.files || []), ...(updatedFiles || [])];
        setUpdatedFile(value);
      } catch (e) {
        captureException(e);
      } finally {
        setFileUploading(false);
      }
    },
    [currentTaskTemplate?.files]
  );
  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    const taskInput: TaskTemplateModel = JSON.parse(JSON.stringify(currentTaskTemplate));
    const updatedFiles = taskInput?.files?.map((file) => {
      return {
        file_type: file.file_type,
        href: file.href,
        size: file.size,
        uploadFileType: FILE_TYPES.UPLOAD,
        name: file.name
      };
    });
    if (!currentTaskTemplate?.templateName || !currentTaskTemplate?.name) {
      dispatchSettingError('Template name and task name are required.');
      return;
    }
    const payload = {
      Title: currentTaskTemplate.templateName,
      TaskTitle: currentTaskTemplate?.name,
      Description: currentTaskTemplate?.description,
      Priority: currentTaskTemplate?.priority,
      Assignee: currentTaskTemplate?.usersIds,
      Attachment: !isEmpty(updatedFiles) ? updatedFiles : []
    };
    try {
      setLoading(true);
      let result;
      if (currentTaskTemplate?.id) {
        result = await updateTaskTemplateDetails(currentTaskTemplate?.id, payload);
      } else {
        result = await createNewTaskTemplate(payload);
        dispatchSettingSuccess('Task template created successfully!');
      }
      if (result) {
        history.push(ROUTES.TEMPLATES_SETTING);
        projectTemplateStore.setCurrentTaskTemplate('');
      }
    } catch (error) {
      captureException(error);
    } finally {
      setLoading(false);
    }
  };

  const onClickClose = useCallback(
    (index: number, file: RawFile) => {
      if (file && !file.uploadFileType && file?.href?.startsWith('https://firebasestorage.googleapis'))
        removeFileFromFirebase(file.href);
      const filesClone = JSON.parse(JSON.stringify(currentTaskTemplate?.files || []));
      filesClone.splice(index, 1);
      setUpdatedFile(filesClone);
      currentTaskTemplate?.setFiles(filesClone);
    },
    [currentTaskTemplate, setUpdatedFile]
  );

  return (
    <>
      <AppLayout>
        <Container className='desktop-content'>
          <Group>
            {!isMobile ? (
              <TaskTemplateHeader>
                <HeaderTitle>
                  <HeaderText>{currentTaskTemplate?.id ? 'Edit Template' : 'New Template'}</HeaderText>
                </HeaderTitle>
                <Button
                  title={currentTaskTemplate?.id ? 'Save Template' : 'Create New'}
                  variant={BUTTON_TYPE.PRIMARY}
                  isLoading={loading}
                  onClick={handleSubmit}
                  size='small'
                />
              </TaskTemplateHeader>
            ) : (
              <ResponsiveHeader title={currentTaskTemplate?.id ? 'Edit Template' : 'New Template'} />
            )}
            <BottomSection>
              <Input
                label='Template Name'
                placeHolder='Add a descriptive name...'
                value={currentTaskTemplate?.templateName}
                onChange={(e) => currentTaskTemplate?.setTemplateName(e.target.value)}
              />
              <Inputs>
                <TextField>
                  <Textarea
                    placeholder='Task Title'
                    rows={1}
                    ref={textareaRef}
                    onInput={handleInput}
                    value={currentTaskTemplate?.name}
                    onChange={(e) => currentTaskTemplate?.setTaskName(e.target.value)}
                  />
                  <DescriptionSection>
                    <TiptapEditor
                      key={currentTaskTemplate?.id || 'new-task-template'}
                      ref={inlineRef}
                      placeholder={'Add Description....'}
                      valueContent={currentTaskTemplate?.description || ''}
                      setValueContent={(value: string) => projectTemplateStore.currentTaskTemplate?.setDescription(value)}
                      isBubbleMenuMaxWidth={true}
                      upload={{
                        setLoading: (value: boolean) => setLoading(value),
                        setStateLoading: (value: boolean) => setFileUploading(value)
                      }}
                    />
                    {currentTaskTemplate?.files &&
                      currentTaskTemplate?.files?.length > 0 &&
                      currentTaskTemplate?.files?.map((file: RawFile, index: number) => {
                        return (
                          <RenderFilePreview
                            key={file?.href}
                            file={file}
                            isClose={true}
                            maxWidth={211}
                            onClose={() => onClickClose(index, file)}
                          />
                        );
                      })}
                  </DescriptionSection>
                </TextField>
                {fileUploading && <UploadDiv>Uploading ({Math.floor(uploadFilePercent)}%)</UploadDiv>}
                <Buttons>
                  <Leftbuttons>
                    <Detail>
                      <AssignBoxV2
                        setIsDropdownOpen={setIsAssignDropdownOpen}
                        projectUsers={currentWorkspace?.users || []}
                        context={{ mode: 'taskTemplate' }}
                        onAssign={assignActions.onAssign}
                        onUnassign={assignActions.onUnassign}
                      />
                    </Detail>
                    <Detail>
                      <PriorityDropdownV2
                        key={`priority-${priorityVersion}`}
                        isCreateTask={true}
                        isTaskTemplate={true}
                        currentTaskTemplate={currentTaskTemplate || undefined}
                      />
                    </Detail>
                  </Leftbuttons>
                  <input
                    type='file'
                    ref={fileInputRef}
                    hidden
                    onChange={onFileUpload}
                    multiple
                    aria-label='Upload file'
                  />
                  <IconButton
                    iconName='file-attachment-icon'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={handleFileInput}
                  />
                </Buttons>
              </Inputs>
            </BottomSection>
          </Group>
        </Container>
        {isAssignDropdownOpen && (
          <OverLayDiv
            role='button'
            className='dropdown-overlay'
            onClick={() => {
              setIsAssignDropdownOpen(false);
            }}
          />
        )}
      </AppLayout>
      <BottomSubNavigation
        rightElement={<BottomNavigationItem iconName='correct-sign-icon' onClick={handleSubmit} />}
      />
    </>
  );
};

export default observer(TaskTemplateDetails);
